/**
 * File Export Utilities
 * Provides functions to export content to various file formats
 */

export interface ExportOptions {
  format: 'docx' | 'xlsx' | 'md' | 'txt';
  content: string;
  filename: string;
}

/**
 * Main export function that routes to appropriate format handler
 */
export async function exportToFile(options: ExportOptions): Promise<void> {
  const { format, content, filename } = options;

  switch (format) {
    case 'docx':
      const { exportToDOCX } = await import('./exportDOCX');
      return exportToDOCX(content, filename);

    case 'xlsx':
      const { exportToXLSX } = await import('./exportXLSX');
      return exportToXLSX(content, filename);

    case 'md':
      return exportToMarkdown(content, filename);

    case 'txt':
      return exportToText(content, filename);

    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/**
 * Export content as Markdown file (no external library needed)
 */
function exportToMarkdown(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  downloadFile(blob, ensureExtension(filename, '.md'));
}

/**
 * Export content as plain text file (strips markdown formatting)
 */
function exportToText(content: string, filename: string): void {
  // Strip markdown syntax for plain text
  let plainText = content
    // Remove headers
    .replace(/^#+\s+/gm, '')
    // Remove bold/italic
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Clean up multiple newlines
    .replace(/\n\n+/g, '\n\n');

  const blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' });
  downloadFile(blob, ensureExtension(filename, '.txt'));
}

/**
 * Helper function to trigger file download in browser
 */
function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Ensure filename has correct extension
 */
function ensureExtension(filename: string, extension: string): string {
  if (!filename.endsWith(extension)) {
    return filename + extension;
  }
  return filename;
}

/**
 * Generate filename from title and date
 */
export function generateFilename(title?: string, extension?: string): string {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const sanitizedTitle = title
    ? title.replace(/[^a-z0-9]/gi, '_').slice(0, 50)
    : 'Export';

  const base = `${sanitizedTitle}_${date}`;
  return extension ? ensureExtension(base, extension) : base;
}
